import { useEffect } from 'react'
import type { JSX } from 'react'
import type { MutableRefObject, KeyboardEvent } from 'react'
import {
  Avatar,
  Flex,
  Heading,
  Input,
  Notice,
  RadioCard,
} from '@buffer-mono/popcorn'

import { getMastodonServers } from '../../utils'

import styles from './Body.module.css'
import { MASTODON_SERVER_GENERIC } from '../../constants'

type BodyProps = {
  setSelectedServer: (value: string) => void
  inputRef: MutableRefObject<HTMLInputElement> | MutableRefObject<null>
  hasError: boolean
  selectedServer: string | null
  setCustomServer: (value: string) => void
}

export function Body({
  setSelectedServer,
  inputRef,
  hasError,
  selectedServer,
  setCustomServer,
}: BodyProps): JSX.Element {
  const availableServers = getMastodonServers()
  const isCustomServerSelected = selectedServer === MASTODON_SERVER_GENERIC

  useEffect(() => {
    if (isCustomServerSelected) {
      inputRef.current?.focus()
    }
  }, [isCustomServerSelected, inputRef])

  const handleCustomInputKeyDown = (
    e: KeyboardEvent<HTMLInputElement>,
  ): void => {
    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault()
      const radioGroup = inputRef.current?.closest('[role="radiogroup"]')
      if (!radioGroup) return

      const items = radioGroup.querySelectorAll<HTMLElement>('[role="radio"]')
      const currentRadio = inputRef.current
        ?.closest('label')
        ?.querySelector<HTMLElement>('[role="radio"]')
      if (!currentRadio) return

      const itemsArray = Array.from(items)
      const idx = itemsArray.indexOf(currentRadio)
      if (idx === -1) return

      const nextIdx =
        e.key === 'ArrowDown'
          ? (idx + 1) % itemsArray.length
          : (idx - 1 + itemsArray.length) % itemsArray.length

      itemsArray[nextIdx]?.click()
      itemsArray[nextIdx]?.focus()
    }
  }

  return (
    <Flex fullWidth direction="column" className={styles.container}>
      <Flex justify="center" fullWidth>
        <Heading size="lg" className={styles.header}>
          Choose your Mastodon server
        </Heading>
      </Flex>
      <Flex justify="center" fullWidth className={styles.cardsContainer}>
        <RadioCard.Group
          onValueChange={(value): void => {
            setSelectedServer(value)
          }}
          className={styles.group}
        >
          {availableServers.map((server) => {
            const showCustomServerInput =
              server.url === MASTODON_SERVER_GENERIC &&
              selectedServer === MASTODON_SERVER_GENERIC

            return (
              <RadioCard
                value={server.url}
                key={server.url}
                className={
                  showCustomServerInput ? styles.customServerCard : undefined
                }
                onFocus={
                  showCustomServerInput
                    ? (e: React.FocusEvent): void => {
                        if (e.target !== inputRef.current) {
                          inputRef.current?.focus()
                        }
                      }
                    : undefined
                }
              >
                <Flex align="center" gap="sm">
                  <Avatar
                    src={server.image_url}
                    alt={`server image for ${server.text}`}
                    size="large"
                  />
                  {showCustomServerInput ? (
                    <Input
                      onChange={(e): void =>
                        setCustomServer(e.target.value.toLowerCase())
                      }
                      onKeyDown={handleCustomInputKeyDown}
                      name="other"
                      ref={inputRef}
                      placeholder="Other"
                      id="custom-server-input"
                      data-testid="custom-server-input"
                    />
                  ) : (
                    <span>
                      <strong>{server.text}</strong>
                    </span>
                  )}
                </Flex>
              </RadioCard>
            )
          })}
          {hasError && (
            <Notice variant="error">
              Mastodon server doesn&apos;t exist or is unreachable
            </Notice>
          )}
        </RadioCard.Group>
      </Flex>
    </Flex>
  )
}
